// Bevétel-kimutatás kártya-elemei: az előző havi VÁLTOZÁS-badge és a TERV–TÉNY érték-pár. (A KPI-kártya maga
// a közös rétegben él: `components/shared/stat-card.tsx` — a Pénzügy Összegzés is használja.)

import { ArrowDownRightIcon, ArrowUpRightIcon } from '@heroicons/react/20/solid';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Text } from '@/components/ui/text';
import { formatPercent } from '@/lib/format-percent';

/** Előző havi változás-badge: zöld ↑ / piros ↓ / semleges 0. `null` → nem renderel (nincs értelmes bázis). */
export function ChangeBadge({ pct }: { pct: number | null }): JSX.Element | null {
  const { i18n } = useTranslation();
  if (pct === null) return null;
  const rounded = Math.round(pct * 10) / 10;
  const variant = rounded > 0 ? 'success' : rounded < 0 ? 'danger' : 'muted';
  const Icon = rounded > 0 ? ArrowUpRightIcon : rounded < 0 ? ArrowDownRightIcon : undefined;
  return (
    <Badge variant={variant} icon={Icon ? <Icon /> : undefined} className="tabular-nums">
      {/* a tizedesjel NYELVFÜGGŐ (hu: vessző, en: pont); a pozitív érték előjelet kap */}
      {formatPercent(rounded, i18n.language, { signed: true })}
    </Badge>
  );
}

/**
 * TERV és TÉNY egymás mellett, az eltérés-badge-dzsel — a kiválasztott-hónap csík jobb oldala.
 *
 * A két szám UGYANAZT a mennyiséget méri (havi eredmény), csak más forrásból: a terv a szerződés-állományból,
 * a tény a kiszámlázott bizonylatokból. Ha a hónapra még nem futott le a generálás, a tény helyén GONDOLATJEL
 * áll, nem nulla: a 0 azt állítaná, hogy semmit sem számláztunk, pedig csak még nem jött el az ideje.
 */
export function PlanVsActualValue({
  planLabel,
  plan,
  actualLabel,
  actual,
  variancePct,
  openLabel,
}: {
  planLabel: string;
  /** A már formázott terv-összeg. */
  plan: string;
  actualLabel: string;
  /** A már formázott tény-összeg, vagy `null`, ha a hónap még nem zárult le. */
  actual: string | null;
  variancePct: number | null;
  /** A „még nem zárult le" felirat (a tény oszlop alatt, ha nincs tény-szám). */
  openLabel: string;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
      <div className="min-w-0">
        <Text variant="secondary" as="div" className="text-xs">
          {planLabel}
        </Text>
        <span className="text-foreground text-xl font-semibold tabular-nums tracking-tight sm:text-2xl">
          {plan}
        </span>
      </div>
      <div className="min-w-0">
        <Text variant="secondary" as="div" className="text-xs">
          {actualLabel}
        </Text>
        {actual === null ? (
          <span className="text-muted-foreground text-sm">{openLabel}</span>
        ) : (
          <span className="text-foreground text-xl font-semibold tabular-nums tracking-tight sm:text-2xl">
            {actual}
          </span>
        )}
      </div>
      {actual !== null && <ChangeBadge pct={variancePct} />}
    </div>
  );
}
